// src/App.jsx — Date Night Planner Frontend
// Connects to the Express backend for live Google Places + email/ICS

import { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const NAMES = ['Vanelys', 'Corey'];
const CATEGORIES = [
  { key: 'dinner',     label: 'Dinner',      icon: '🍽️' },
  { key: 'movies',     label: 'Movies',      icon: '🎬' },
  { key: 'breakfast',  label: 'Breakfast',   icon: '☀️' },
  { key: 'outdoors',   label: 'Outdoors',    icon: '🌿' },
  { key: 'activities', label: 'Activities',  icon: '🎨' },
];
const CITIES = [
  { val: 'all',     label: 'All Cities' },
  { val: 'pompano', label: 'Pompano Beach' },
  { val: 'ftl',     label: 'Fort Lauderdale' },
  { val: 'miami',   label: 'Miami' },
  { val: 'wpb',     label: 'West Palm Beach' },
];

export default function App() {
  const [nameIdx, setNameIdx]       = useState(0);
  const [category, setCategory]     = useState('dinner');
  const [city, setCity]             = useState('all');
  const [budget, setBudget]         = useState('all');
  const [cuisine, setCuisine]       = useState('all');
  const [page, setPage]             = useState(1);
  const [venues, setVenues]         = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState(null);
  const [availability, setAvail]    = useState([]);
  const [chosenSlot, setChosenSlot] = useState('');
  const [dateVal, setDateVal]       = useState(new Date().toISOString().split('T')[0]);
  const [email, setEmail]           = useState('');
  const [emailSent, setEmailSent]   = useState(false);
  const [toast, setToast]           = useState('');
  const modalRef = useRef(null);

  // Cycle greeting names every 3 seconds
  useEffect(() => {
    const timer = setInterval(() => setNameIdx(i => (i + 1) % NAMES.length), 3000);
    return () => clearInterval(timer);
  }, []);

  // Fetch venues from backend
  const fetchVenues = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ category, city, cuisine, budget, page });
      const res = await fetch(`${API}/api/places?${params}`);
      const data = await res.json();
      setVenues(data.results || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalResults || 0);
    } catch (err) {
      console.error('Failed to fetch venues:', err);
      showToast('⚠️ Could not load venues. Check your API keys.');
    } finally {
      setLoading(false);
    }
  }, [category, city, cuisine, budget, page]);

  useEffect(() => { fetchVenues(); }, [fetchVenues]);

  // Fetch availability when modal opens
  const openVenue = async (venue) => {
    setSelected(venue);
    setEmailSent(false);
    try {
      const res = await fetch(`${API}/api/availability?venueId=${venue.placeId}&date=${dateVal}`);
      const data = await res.json();
      setAvail(data.available || []);
      setChosenSlot(data.available?.[0] || '');
    } catch {
      setAvail(['6:00 PM', '7:30 PM', '9:00 PM']);
      setChosenSlot('6:00 PM');
    }
  };

  const sendEmail = async () => {
    if (!email) return showToast('Please enter your email address.');
    try {
      const res = await fetch(`${API}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue: selected, slot: chosenSlot, date: dateVal, recipientEmail: email }),
      });
      if (res.ok) { setEmailSent(true); showToast('✉️ Confirmation email sent!'); }
      else showToast('⚠️ Could not send email. Check SendGrid settings.');
    } catch { showToast('⚠️ Email service unavailable.'); }
  };

  const downloadICS = async () => {
    try {
      const res = await fetch(`${API}/api/generate-ics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ venue: selected, slot: chosenSlot, date: dateVal }),
      });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `date-night-${selected.name.replace(/\s+/g,'-').toLowerCase()}.ics`;
      a.click();
      showToast('📅 Calendar invite downloaded!');
    } catch { showToast('⚠️ Could not generate calendar invite.'); }
  };

  const surpriseMe = async () => {
    const cats = CATEGORIES.map(c => c.key);
    const randCat = cats[Math.floor(Math.random() * cats.length)];
    setCategory(randCat); setCity('all'); setBudget('all'); setCuisine('all'); setPage(1);
    showToast('✨ Surprise! We picked something special for you.');
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const renderStars = (rating) => {
    const full = Math.floor(rating); const half = rating % 1 >= 0.5;
    return Array.from({ length: 5 }, (_, i) => {
      if (i < full) return <span key={i} className="star full">★</span>;
      if (i === full && half) return <span key={i} className="star half">★</span>;
      return <span key={i} className="star empty">★</span>;
    });
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-eyebrow">South Florida · Date Night</div>
        <h1 className="header-title">
          Plan your next date<br />
          with <em className="greeting">{NAMES[nameIdx]}</em>
        </h1>
        <p className="header-sub">Curated experiences across Pompano Beach, Fort Lauderdale, Miami & West Palm Beach</p>
      </header>

      {/* Nav */}
      <nav className="nav">
        {CATEGORIES.map(cat => (
          <button key={cat.key} className={`nav-tab${category === cat.key ? ' active' : ''}`}
            onClick={() => { setCategory(cat.key); setPage(1); }}>
            {cat.icon} {cat.label}
          </button>
        ))}
        <button className="surprise-btn" onClick={surpriseMe}>✨ Surprise Me</button>
      </nav>

      {/* Filters */}
      <div className="filters">
        <span className="filter-label">City:</span>
        <select value={city} onChange={e => { setCity(e.target.value); setPage(1); }}>
          {CITIES.map(c => <option key={c.val} value={c.val}>{c.label}</option>)}
        </select>
        <span className="filter-label">Budget:</span>
        {['all', '$', '$$', '$$$'].map(b => (
          <button key={b} className={`chip${budget === b ? ' active' : ''}`}
            onClick={() => { setBudget(b); setPage(1); }}>{b === 'all' ? 'All' : b}</button>
        ))}
        {category === 'dinner' && <>
          <span className="filter-label">Cuisine:</span>
          {['all', 'seafood', 'peruvian', 'mexican', 'american', 'italian'].map(c => (
            <button key={c} className={`chip${cuisine === c ? ' active' : ''}`}
              onClick={() => { setCuisine(c); setPage(1); }}>
              {c === 'all' ? 'All' : c.charAt(0).toUpperCase() + c.slice(1)}
            </button>
          ))}
        </>}
      </div>

      {/* Main */}
      <main className="main">
        <div className="section-meta">
          <span className="section-title">{category.toUpperCase()} · SOUTH FLORIDA</span>
          <span className="count-label">{totalCount} options found</span>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner" />
            <p>Finding the perfect spots...</p>
          </div>
        ) : (
          <div className="cards-grid">
            {venues.map(v => (
              <div key={v.id} className="card" onClick={() => openVenue(v)}>
                <div className="card-img"><span>{v.emoji || '🍽️'}</span>
                  <span className="card-price">{v.price}</span>
                </div>
                <div className="card-body">
                  <div className="card-name">{v.name}</div>
                  <div className="card-addr">📍 {v.address.split(',').slice(0,2).join(',')}</div>
                  <div className="stars">{renderStars(v.rating)} <span className="rating-num">{v.rating}</span></div>
                  {v.topReview && <div className="review">{v.topReview.slice(0, 120)}…</div>}
                  <div className="card-footer">
                    <div className="slots-preview">{(v.slots || ['7:00 PM','8:30 PM']).slice(0,3).map(s =>
                      <span key={s} className="slot">{s}</span>)}</div>
                    <button className="reserve-btn" onClick={e => { e.stopPropagation(); openVenue(v); }}>Reserve</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="pagination">
          <button className="pg-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span className="pg-info">Page {page} of {totalPages}</span>
          <button className="pg-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      </main>

      {/* Modal */}
      {selected && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="modal" ref={modalRef}>
            <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            <div className="modal-emoji">{selected.emoji || '🍽️'}</div>
            <h2 className="modal-name">{selected.name}</h2>
            <a className="modal-addr"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.address)}`}
              target="_blank" rel="noreferrer">
              📍 {selected.address}
            </a>
            <p className="modal-desc">{selected.description}</p>

            <div className="modal-section-label">Select a Time</div>
            <div className="modal-slots">
              {availability.map(s => (
                <div key={s} className={`modal-slot${chosenSlot === s ? ' chosen' : ''}`}
                  onClick={() => setChosenSlot(s)}>{s}</div>
              ))}
            </div>

            <div className="modal-section-label" style={{ marginTop: '1rem' }}>Date</div>
            <input type="date" className="date-input" value={dateVal}
              min={new Date().toISOString().split('T')[0]}
              onChange={e => setDateVal(e.target.value)} />

            <div className="modal-section-label" style={{ marginTop: '1rem' }}>Email for confirmation</div>
            <input type="email" className="email-input" placeholder="your@email.com"
              value={email} onChange={e => setEmail(e.target.value)} />

            <div className="modal-actions">
              <button className="btn-primary" onClick={sendEmail}>✉️ Send Confirmation Email</button>
              <button className="btn-secondary" onClick={downloadICS}>📅 Download .ics Calendar Invite</button>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.address)}`}
                target="_blank" rel="noreferrer">
                <button className="btn-secondary" style={{ width: '100%' }}>🗺️ Open in Google Maps</button>
              </a>
              {selected.resyLink && (
                <a href={selected.resyLink} target="_blank" rel="noreferrer">
                  <button className="btn-primary" style={{ background: '#B8935A' }}>🍽️ Complete Reservation</button>
                </a>
              )}
            </div>
            {emailSent && <div className="sent-msg">✅ Email sent! Check your inbox for all the details.</div>}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
